import type { PermissionMapping } from './permissions-types';

// API route permissions: developer, Stripe, company, API keys, lists, saved searches, watchlist, alerts
export const ACCOUNT_API_PERMISSIONS: PermissionMapping[] = [
  // Developer / Subscription Event APIs — open to all authenticated users;
  // each handler enforces per-user scoping.
  {
    path: '/api/developer/subscriptions/events',
    permission: null,
    name: 'View Subscription Events API',
    description: 'Access to subscription event history API',
  },
  {
    path: '/api/developer/subscriptions/events/[eventId]',
    permission: null,
    name: 'View Subscription Event Detail API',
    description: 'Access to single subscription event detail',
  },
  {
    path: '/api/developer/subscriptions/events/[eventId]/resend',
    permission: null,
    name: 'Resend Subscription Event API',
    description: 'Re-deliver a subscription event',
  },
  {
    path: '/api/developer/subscriptions',
    permission: null,
    name: 'List Subscriptions API',
    description: 'List the current user’s subscriptions',
  },
  {
    path: '/api/developer/subscriptions/[subscriptionId]',
    permission: null,
    name: 'Delete Subscription API',
    description: 'Delete one of the current user’s subscriptions',
  },

  // Stripe APIs (these may need special handling, but include for completeness)
  {
    // Any signed-in account may buy credits; apiHandler refuses guests. Not
    // keyed on account.billing: the route never checked it before moving to
    // apiHandler, and no account may be locked out of buying credits.
    path: '/api/stripe/create-checkout-session',
    permission: null,
    name: 'Create Checkout Session',
    description: 'Start a credit package, top-up or card-setup checkout',
  },
  {
    // The checkout modal switching its open session to another amount; the
    // route checks the session belongs to the caller.
    path: '/api/stripe/update-checkout-session',
    permission: null,
    name: 'Update Checkout Session',
    description: 'Switch the open checkout to another credit package or amount',
  },
  {
    path: '/api/account/auto-topup',
    permission: null,
    name: 'Auto Top-up Settings',
    description: 'Read and change the current user’s auto top-up',
  },
  {
    path: '/api/stripe/create-portal-session',
    permission: 'account.billing',
    name: 'Create Portal Session',
    description: 'Access to create portal session',
  },
  {
    path: '/api/stripe/invoices',
    permission: 'account.billing',
    name: 'View Invoices',
    description: 'Access to invoices API',
  },
  {
    path: '/api/stripe/products',
    permission: 'admin.view',
    name: 'View Products',
    description: 'Access to products API',
  },
  {
    path: '/api/stripe/subscription',
    permission: 'account.billing',
    name: 'Manage Subscription',
    description: 'Access to subscription API',
  },
  {
    path: '/api/stripe/verify-session',
    permission: 'account.billing',
    name: 'Verify Session',
    description: 'Access to verify session API',
  },

  // Company APIs - Unified Endpoints (companies.view permission)
  {
    path: '/api/companies/[euId]/unified/basic',
    permission: null,
    name: 'View Company Basic Info',
    description: 'Public access to unified company basic information API',
  },
  {
    path: '/api/companies/[euId]/dataset',
    permission: null,
    name: 'View Company Dataset',
    description:
      'Public machine-readable dataset of company basic info (referenced from schema.org Dataset JSON-LD)',
  },
  {
    path: '/api/companies/[euId]/unified/register',
    permission: null,
    name: 'View Company Register Info',
    description:
      'Access to unified company register information API (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/unified/financials',
    permission: null,
    name: 'View Company Financials',
    description:
      'Access to unified company financial data API (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/financial-tables/raw',
    permission: 'companies.view',
    name: 'View Company Raw Financials',
    description: 'Access to company raw financial data API',
  },

  // Company APIs - Contact
  {
    path: '/api/companies/[euId]/unified/contact',
    permission: null,
    name: 'View Company Contact',
    description: 'Access to unified company contact information API',
  },
  {
    path: '/api/companies/[euId]/unified/gesellschafter',
    permission: null,
    name: 'View Company Gesellschafter',
    description: 'Access to unified company gesellschafter information API',
  },

  // Company APIs - Register docs & Publications (all logged-in users)
  {
    path: '/api/companies/[euId]/network',
    permission: null,
    name: 'View Company Network',
    description: 'Access to company relationship network graph',
  },
  {
    path: '/api/person',
    permission: null,
    name: 'View Person Profile',
    description: 'Access to person profile and their company connections',
  },
  {
    path: '/api/companies/[euId]/files',
    permission: null,
    name: 'View Company Files',
    description:
      'Access to company publications listing API (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/files/[fileId]/download',
    permission: null,
    name: 'Download Company File',
    description:
      'Access to download company publications (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/register-file/download',
    permission: null,
    name: 'Download Register File',
    description:
      'Scrape and download a single register file type (all logged-in users)',
  },
  {
    path: '/api/companies/[euId]/files/[fileId]/content',
    permission: null,
    name: 'View Company File Content',
    description: 'Access to view company file content (all logged-in users)',
  },

  // API Key management — open to all authenticated users; handlers scope by user.
  {
    path: '/api/account/api',
    permission: null,
    name: 'Manage API Keys',
    description: 'Create and list API keys',
  },
  {
    path: '/api/account/api/[id]',
    permission: null,
    name: 'Modify API Key',
    description: 'Revoke or update an API key',
  },
  {
    path: '/api/account/usage',
    permission: null,
    name: 'View API Usage',
    description:
      'Per-day API request and credit usage (handler enforces per-user scoping)',
  },

  // Lists APIs — any authenticated user; plan caps enforced in the handlers
  // (the plan lives in the auth DB, which the backend can't see).
  {
    path: '/api/lists',
    permission: null,
    name: 'My Lists',
    description: "List or create the authenticated user's company lists",
  },
  {
    path: '/api/lists/[listId]',
    permission: null,
    name: 'Modify List',
    description: 'Read, rename, configure or delete one owned list',
  },
  {
    path: '/api/lists/[listId]/companies/[euId]',
    permission: null,
    name: 'Modify List Companies',
    description: 'Add or remove a company on one owned list',
  },
  {
    path: '/api/lists/[listId]/companies/bulk',
    permission: null,
    name: 'Bulk Add List Companies',
    description: 'Import-wizard commit: add many companies to one owned list',
  },
  {
    path: '/api/lists/import/match',
    permission: null,
    name: 'Match List Import Rows',
    description: 'Match spreadsheet rows to companies for the import wizard',
  },
  {
    path: '/api/lists/import/fetch-url',
    permission: null,
    name: 'Fetch List Import File',
    description: 'Server-side fetch of a CSV/XLSX URL for the import wizard',
  },
  {
    path: '/api/lists/[listId]/share',
    permission: null,
    name: 'Share List',
    description: 'Mint or revoke the public share token of one owned list',
  },
  {
    path: '/api/lists/[listId]/watch',
    permission: null,
    name: 'List Watch',
    description: 'Configure change alerts for one owned list',
  },

  // Saved-search APIs — any authenticated user. Unlike Lists there is no
  // Pro gate: every tier may save, only the count differs, so the cap is
  // enforced per-handler rather than by a feature check.
  {
    path: '/api/saved-searches',
    permission: null,
    name: 'Saved Searches',
    description: "List or create the authenticated user's saved searches",
  },
  {
    path: '/api/saved-searches/[id]',
    permission: null,
    name: 'Modify Saved Search',
    description: 'Rename, repoint or delete one owned saved search',
  },
  {
    path: '/api/saved-searches/[id]/opened',
    permission: null,
    name: 'Open Saved Search',
    description: 'Advance the new-matches baseline after viewing results',
  },
  {
    path: '/api/saved-searches/[id]/to-list',
    permission: null,
    name: 'Saved Search To List',
    description: 'Materialise a saved search as a company list (Pro)',
  },
  {
    path: '/api/saved-searches/refresh-counts',
    permission: null,
    name: 'Refresh Saved Search Counts',
    description: 'Remeasure match counts for a batch of saved searches',
  },

  // Watchlist APIs — any authenticated user; backend keys entries by Auth0 sub.
  {
    path: '/api/watch',
    permission: null,
    name: 'Watch Overview',
    description: 'Everything the user watches — companies and lists — plus the tier budget',
  },
  {
    path: '/api/watchlist',
    permission: null,
    name: 'View Watchlist',
    description: "List the authenticated user's company watchlist",
  },
  {
    path: '/api/watch/company/[euId]',
    permission: null,
    name: 'Configure Company Watch',
    description:
      'Read or replace the alert criteria, recipients and cadence for one watched company',
  },
  {
    path: '/api/watchlist/[euId]',
    permission: null,
    name: 'Modify Watchlist',
    description:
      "Add or remove a company from the authenticated user's watchlist",
  },
  {
    path: '/api/watchlist/notifications',
    permission: null,
    name: 'View Watchlist Notifications',
    description: "List the authenticated user's watchlist notifications",
  },
  {
    path: '/api/notifications',
    permission: null,
    name: 'View Notifications',
    description:
      'List every notification for the authenticated user (the bell)',
  },
  {
    path: '/api/notifications/[notificationId]',
    permission: null,
    name: 'Mark Notification Read',
    description: 'Mark a single notification as read',
  },
  {
    path: '/api/notifications/read-all',
    permission: null,
    name: 'Mark All Notifications Read',
    description: 'Mark every notification for the caller as read',
  },
  {
    path: '/api/account/notification-settings',
    permission: null,
    name: 'Notification Settings',
    description:
      "Read and update the caller's alert-email, credit-email and email-language settings",
  },

  // Unified alerts — any authenticated user; the backend keys every
  // subscription by Auth0 sub and prices it in credits per day.
  {
    path: '/api/alerts/pricing',
    permission: null,
    name: 'Alert Pricing',
    description: 'Daily credit rates for company, list and feed alerts',
  },
  {
    path: '/api/alerts/cost',
    permission: null,
    name: 'Alert Cost',
    description: "The caller's daily alert cost and how long the balance lasts",
  },
  {
    path: '/api/alerts/quote',
    permission: null,
    name: 'Alert Quote',
    description: 'Price a proposed or edited alert before saving it',
  },
  {
    path: '/api/alerts/subscriptions',
    permission: null,
    name: 'Alert Subscriptions',
    description: "List the caller's alerts or create a feed alert",
  },
  {
    path: '/api/alerts/subscriptions/[publicId]',
    permission: null,
    name: 'Modify Alert Subscription',
    description: 'Edit, pause, resume or delete one owned alert',
  },
  {
    path: '/api/alerts/settings',
    permission: null,
    name: 'Alert Settings',
    description: "Read and update the caller's backend alert-mail settings",
  },
  {
    path: '/api/account/downloads',
    permission: null,
    name: 'View Downloads',
    description: "List the authenticated user's downloaded documents",
  },
  {
    path: '/api/companies/logos',
    permission: null,
    name: 'Company Logos',
    description: 'Batch company logos for guests and authenticated users',
  },
  {
    path: '/api/companies/newly-founded',
    permission: null,
    name: 'Newly Founded Companies',
    description: 'Public feed of newly founded companies',
  },
  {
    path: '/api/account/recent-views',
    permission: null,
    name: 'Recent Company Views',
    description: "List or record the authenticated user's recent company views",
  },
  {
    path: '/api/account/recent-searches',
    permission: null,
    name: 'Recent Searches',
    description: "List or record the authenticated user's recent searches",
  },
  {
    path: '/api/documents/status',
    permission: null,
    name: 'Document Status',
    description:
      'Current version / withdrawal state of documents, by resource key',
  },
  {
    path: '/api/watchlist/notifications/[notificationId]',
    permission: null,
    name: 'Mark Watchlist Notification Read',
    description: 'Mark a single watchlist notification as read',
  },
  {
    path: '/api/watchlist/notifications/read-all',
    permission: null,
    name: 'Mark All Watchlist Notifications Read',
    description: 'Mark every watchlist notification as read',
  },
];
